import React, { useEffect, useRef, useState } from "react";
import lottie from "lottie-web";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { getOngoingTransaction, sendInvoice, stopCharging, unlockConnector } from "../services/apiService";
import DownloadAppScreen from "./DownloadAppScreen";
import { useErrorHandler } from "../utils/errorHandler";
import analyticsService from "../services/analyticsService";

// modal de confirmation
const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message }) => {
  const { t } = useTranslation();
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
        <h3 className="text-lg font-medium mb-4">{title}</h3>
        <p className="text-gray-600 mb-6">{message}</p>
        <div className="flex justify-center space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            {t("startCharging.cancel")}
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            {t("startCharging.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function StartChargingComponent() {
  const { t } = useTranslation();
  const { handleError } = useErrorHandler();
  const navigate = useNavigate();
  const location = useLocation();
  const { paymentMethodId, accessToken, connector, evcpData, paymentIntentId, finishedTransaction } = location.state || {};
  
  // Vérification des informations nécessaires.
  // accessToken est toujours requis. evcpData ne l'est que pour une session en cours
  // démarrée via le parcours normal (arrêt/déverrouillage) ; les reprises via lien
  // email ou re-scan (récap de session terminée) n'en disposent pas.
  useEffect(() => {
    if (!accessToken) {
      navigate("/error", {
        state: {
          error: t("startCharging.errors.missingInfo"),
          message: t("startCharging.errors.missingInfoMessage")
        }
      });
    }
  }, [accessToken, navigate, t]);

  useEffect(() => {
    const animationContainer = document.getElementById("lottie-animation");

    const animation = lottie.loadAnimation({
      container: animationContainer,
      renderer: "svg",
      loop: true,
      autoplay: true,
      path: "/charging_animation.json",
    });
    return () => animation.destroy(); // Clean up on unmount
  }, []);

  const [showInvoiceFields, setShowInvoiceFields] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFacture, setShowFacture] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [adressPayload, setAdressPayload] = useState({});
  const [isFocused, setIsFocused] = useState(false);
  const [transactionData, setTransactionData] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [chargingStarted, setChargingStarted] = useState(false);
  const [chargingFinished, setChargingFinished] = useState(false);
  const [finalTransactionData, setFinalTransactionData] = useState(null);
  const [currentDuration, setCurrentDuration] = useState("0h 0min");
  const [endChargeTime, setEndChargeTime] = useState(null);
  const [energyConsumed, setEnergyConsumed] = useState(0);
  const transactionIdRef = useRef(0);
  const finalEnergyConsumedRef = useRef(0);
  const [startChargeTriggered, setStartChargeTriggered] = useState(false);
  const [isStoppingCharge, setIsStoppingCharge] = useState(false);
  const [isUnlocking, setIsUnlocking] = useState(false);
  const pollingIntervalRef = useRef(null);
  const durationIntervalRef = useRef(null);
  const fromSelectionRef = useRef(false);
  const autocompleteService = useRef(null);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [showStopChargeModal, setShowStopChargeModal] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [finalDuration, setFinalDuration] = useState(null);
  const [startTime, setStartTime] = useState(null);

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    adresse: "",
  });

  const calculateDuration = (startDate, endDate = null) => {
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();
    const diffMs = end - start;
    const diffMins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h ${mins}min`;
  };

  const convertToKWh = (meterValue) => {
    // Si meterValue est en Wh, diviser par 1000
    // Si meterValue est déjà en kWh, retourner la valeur telle quelle
      return meterValue / 1000 ;
  };

  // Fonction pour démarrer le suivi de la transaction
  const startTransactionTracking = () => {
    if (!accessToken) return;
    
    setIsPolling(true);
    setChargingStarted(true);
    
    // Premier appel immédiat
    fetchTransactionData();
    
    // Puis configurer l'intervalle pour les appels suivants
    pollingIntervalRef.current = setInterval(fetchTransactionData, 30000); // Polling toutes les 30 secondes
  };

  // afficher le modal de confirmation d'arret
  const handleStopCharge = () => {
    
    if (!evcpData?.reference || !accessToken) {
      const error = new Error("Informations manquantes pour arrêter la charge");
      const errorMessage = handleError(error);
      setError(errorMessage);
      return;
    }

    setShowStopChargeModal(true);
  };

  // afficher le modal de confirmation de unlock
  const handleUnlock = () => {
    if (!evcpData?.reference || !connector?.number || !accessToken) {
      const error = new Error("Informations manquantes pour déverrouiller le connecteur");
      const errorMessage = handleError(error);
      setError(errorMessage);
      return;
    }

    setShowUnlockModal(true);
  };

  // Fonction pour confirmer et exécuter l'arrêt de la charge
  const confirmStopCharge = async () => {
    setShowStopChargeModal(false);
    setIsStoppingCharge(true);
    analyticsService.pushEvent('charge_stopped_by_user', {
      station_id: evcpData?.reference,
      connector_id: connector?.reference,
      energy_kwh: finalEnergyConsumedRef.current,
    });

    try {
      await stopCharging(evcpData.reference, accessToken);
    } catch (error) {
      console.error("Erreur lors de l'arrêt de la charge:", error);
      
      const errorMessage = handleError(error);
      setError(errorMessage);
      setIsStoppingCharge(false);
    }
  };

  // confirmer et lancer le unlock
  const confirmUnlock = async () => {
    setShowUnlockModal(false);
    setIsUnlocking(true);
    
    try {
      await unlockConnector(evcpData.reference, connector.number, accessToken);
      console.log("Connecteur déverrouillé avec succès");
    } catch (error) {
      console.error("Erreur lors du déverrouillage du connecteur:", error);
      
      const errorMessage = handleError(error);
      setError(errorMessage);
    } finally {
      setIsUnlocking(false);
    }
  };

  // Fonction pour récupérer les données de transaction
  const fetchTransactionData = async () => {
    try {
      const data = await getOngoingTransaction(accessToken);
      
      // Mettre à jour l'énergie consommée
      if (data?.ongoingTransactionInfo?.meterValue !== undefined) {
        const energy = convertToKWh(data.ongoingTransactionInfo.meterValue);
        setEnergyConsumed(energy);
        
        finalEnergyConsumedRef.current = energy;
        
        if (data.ongoingTransactionInfo.transactionId) {
          transactionIdRef.current = data.ongoingTransactionInfo.transactionId;
        }
      }
      
      setTransactionData(data);

      // Vérifier si la charge est terminée (ongoingTransactionInfo et ongoingCpSession sont null)
      if (data && !data.ongoingTransactionInfo && !data.ongoingCpSession) {
        setChargingFinished(true);
        const endTime = new Date();
        setEndChargeTime(endTime);
        setIsPolling(false);
        clearInterval(pollingIntervalRef.current);
        clearInterval(durationIntervalRef.current);
        setFinalTransactionData(data);
        
        // Calculer la durée finale
        if (startTime) {
          const finalDur = calculateDuration(startTime, endTime);
          setFinalDuration(finalDur);
        }
        
        if (isStoppingCharge) {
          setIsStoppingCharge(false);
        }
        
        console.log("La charge est terminée");
      }
    } catch (error) {
      console.error("Erreur lors du polling de la transaction:", error);

      const errorMessage = handleError(error);
      setError(errorMessage);
      // Continuer le polling même en cas d'erreur
    }
  };

  useEffect(() => {
    // On définit la fonction de callback AVANT de charger le script
    window.initGoogleMaps = () => {
      console.log("Google Maps script a été chargé avec succès !");
      setIsGoogleLoaded(true);
      if (window.google && window.google.maps) {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
      }
    };

    // On crée l'élément <script>
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_PLACES_API_KEY}&libraries=places&callback=initGoogleMaps`;
    script.async = true;
    script.defer = true;

    document.body.appendChild(script);

    // nettoyage pour éviter les fuites mémoire
    return () => {
      document.body.removeChild(script);
      delete window.initGoogleMaps;
    };
  }, []);

  useEffect(() => {
    if (!accessToken || !isPolling) return;

    const fetchTransactionData = async () => {
      try {
        const data = await getOngoingTransaction(accessToken);
        
        if (data?.ongoingTransactionInfo?.meterValue !== undefined) {
          const energy = convertToKWh(data.ongoingTransactionInfo.meterValue);
          finalEnergyConsumedRef.current = energy;
          setEnergyConsumed(energy);
          
          if (data.ongoingTransactionInfo?.transactionId) {
            transactionIdRef.current = data.ongoingTransactionInfo.transactionId;
          }
        }

        setTransactionData(data);

        const hasOngoing = !!data?.ongoingTransactionInfo;

        if (!chargingStarted && hasOngoing) {
          setChargingStarted(true);
          analyticsService.pushPageView('charging_in_progress', {
            station_id: evcpData?.reference,
            connector_id: connector?.reference,
          });

          // Stocker l'heure de début de charge
          if (data.ongoingTransactionInfo && data.ongoingTransactionInfo.start) {
            setStartTime(data.ongoingTransactionInfo.start);
          }

          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = setInterval(fetchTransactionData, 30000);
        }

        // Charge terminee
        if (
          chargingStarted &&
          !data?.ongoingTransactionInfo &&
          !data?.ongoingCpSession
        ) {
          setChargingFinished(true);
          const endTime = new Date();
          setEndChargeTime(endTime);
          setIsPolling(false);
          setFinalTransactionData(data);

          clearInterval(pollingIntervalRef.current);
          clearInterval(durationIntervalRef.current);

          // Calculer la durée finale
          if (startTime) {
            const finalDur = calculateDuration(startTime, endTime);
            setFinalDuration(finalDur);
          }

          analyticsService.pushPageView('charging_completed', { station_id: evcpData?.reference });
          analyticsService.pushEvent('charge_completed', {
            station_id: evcpData?.reference,
            connector_id: connector?.reference,
            transaction_id: transactionIdRef.current,
            energy_kwh: finalEnergyConsumedRef.current,
          });

          if (isStoppingCharge) {
            setIsStoppingCharge(false);
          }
        }
      } catch (e) {
        console.error("Erreur polling:", e);
        
        const errorMessage = handleError(e);
        setError(errorMessage);
      }
    };

    fetchTransactionData();

    pollingIntervalRef.current = setInterval(fetchTransactionData, 5000);

    return () => {
      clearInterval(pollingIntervalRef.current);
    };
  }, [accessToken, isPolling, chargingStarted, startTime, isStoppingCharge]);

  useEffect(() => {
    if (chargingStarted && !chargingFinished && startTime) {
      // Mettre à jour la durée chaque seconde
      durationIntervalRef.current = setInterval(() => {
        setCurrentDuration(calculateDuration(startTime));
      }, 1000);
      
      setCurrentDuration(calculateDuration(startTime));
    } else {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
        durationIntervalRef.current = null;
      }
    }

    return () => {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    };
  }, [chargingStarted, chargingFinished, startTime]);

  useEffect(() => {
    if (startChargeTriggered && accessToken) {
      setIsPolling(true);
    }
  }, [startChargeTriggered, accessToken]);

  useEffect(() => {
    if (!isGoogleLoaded || !autocompleteService.current || fromSelectionRef.current) {
      fromSelectionRef.current = false;
      return;
    }

    if (query.length < 3) {
      setSuggestions([]);
      return;
    }

    const request = {
      input: query,
      types: ["address"],
    };

    autocompleteService.current.getPlacePredictions(request, async (predictions, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
        const placesSuggestions = predictions.map((place) => ({
          properties: {
            id: place.place_id,
            label: place.description,
            city: place.structured_formatting
              ? place.structured_formatting.secondary_text
              : place.terms.slice(0, -1)[0]?.value || "",
            citycode: "",
            postcode: place.structured_formatting
              ? place.structured_formatting.secondary_text
              : "",
          },
        }));
        
        setSuggestions(placesSuggestions);
        setAdressPayload(placesSuggestions);
      } else {
        console.error("Google Places API returned status:", status);
        // En cas d'erreur avec Google Places, on peut essayer l'API data.gouv.fr en fallback
        try {
          const fallbackResponse = await axios.get(
            "https://api-adresse.data.gouv.fr/search/",
            {
              params: {
                q: query,
                autocomplete: 1,
                limit: 10,
              },
            }
          );
          setSuggestions(fallbackResponse.data.features);
          setAdressPayload(fallbackResponse.data.features);
        } catch (fallbackErr) {
          console.error("Erreur avec l'API de fallback:", fallbackErr);
          
          const errorMessage = handleError(fallbackErr);
          setError(errorMessage);
        }
      }
    });
  }, [query, isGoogleLoaded]);

  const handleSelect = (address) => {
    setAdressPayload(address);
    fromSelectionRef.current = true;
    setQuery(address.properties.label);
    setSuggestions([]);
    setIsFocused(false);
  };

  const handleCheckboxChange = (e) => {
    if (e.target.checked) analyticsService.pushEvent('invoice_requested');
    setShowInvoiceFields(e.target.checked);
    setError("");
  };
  
  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleValider = async () => {
    if (showInvoiceFields) {
      const errors = {};
      if (!formData.first_name) errors.first_name = t("startCharging.errors.firstNameRequired");
      if (!formData.last_name) errors.last_name = t("startCharging.errors.lastNameRequired");
      if (!query) errors.adresse = t("startCharging.errors.addressRequired");

      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        return;
      }
    }

    setFieldErrors({});
    
    // api envoi de facture
    if (showInvoiceFields && paymentIntentId) {
      try {
        // Extraire les informations de l'adresse
        let street = '', city = '', postalCode = 0;
        
        if (adressPayload && adressPayload.properties && adressPayload.properties.label) {
          const addressParts = adressPayload.properties.label.split(',');
          street = addressParts[0] || '';
          
          if (addressParts.length > 1) {
            const cityAndPostal = addressParts[1] || '';
            const cityParts = cityAndPostal.trim().split(' ');
            postalCode = parseInt(cityParts[0]) || 0;
            city = cityParts.slice(1).join(' ') || '';
          }
        }
        
        // Déterminer le code pays (FRA par défaut)
        const country = "FRA";

        const invoiceData = {
          firstName: formData.first_name,
          lastName: formData.last_name,
          country,
          street,
          postalCode,
          city: adressPayload.properties.city,
        };

        
        if (!transactionIdRef.current) {
          console.error("ERREUR: transactionId est 0 ou undefined!");
          setError("Erreur: ID de transaction manquant. Veuillez réessayer.");
          return;
        }

        const invoice = await sendInvoice(paymentIntentId, invoiceData, accessToken, transactionIdRef.current);
        analyticsService.pushEvent('invoice_sent');

        setShowFacture(true);
        setShowInvoiceFields(false);
        setShowSuccess(true);
      } catch (error) {
        console.error("Erreur lors de l'envoi de la facture:", error);
        analyticsService.pushEvent('invoice_failed', { error_type: error?.response?.status || 'unknown' });
        const errorMessage = handleError(error);
        setError(errorMessage);
      }
    } else {
      setShowFacture(true);
      setShowInvoiceFields(false);
      setShowSuccess(true);
    }
  };

  const isDisabled =
    showInvoiceFields && (
      !formData.first_name.trim() ||
      !formData.last_name.trim() ||
      !query.trim() ||
      !adressPayload || !adressPayload.properties || !adressPayload.properties.label ||
      query !== (adressPayload.properties ? adressPayload.properties.label : ''));

  const handleDownloadApp = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    if (/android/i.test(userAgent)) {
      analyticsService.pushEvent('app_download_tapped', { platform: 'android' });
      window.open('https://play.google.com/store/apps/details?id=com.qovoltis.client', '_blank');
    } else if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      analyticsService.pushEvent('app_download_tapped', { platform: 'ios' });
      window.open('https://apps.apple.com/fr/app/qovoltis/1524983586', '_blank');
    } else {
      analyticsService.pushEvent('app_download_tapped', { platform: 'ios' });
      window.open('https://apps.apple.com/fr/app/qovoltis/1524983586', '_blank');
    }
  };

  const displayData = chargingFinished && finalTransactionData ? finalTransactionData : transactionData;
  const isCurrentlyCharging = chargingStarted && !chargingFinished;

  const simulateStartCharge = () => {
    setStartChargeTriggered(true);
  };

  useEffect(() => {
    // Reprise d'une session déjà terminée : pas de polling, on attend l'init du récap
    if (finishedTransaction) return;

    const timer = setTimeout(() => {
      simulateStartCharge();
    }, 2000);

    return () => clearTimeout(timer);
  }, [finishedTransaction]);

  // Reprise d'une session déjà terminée (re-scan QR / lien email) :
  // on pré-remplit l'état "terminée" à partir du récap renvoyé par l'API, sans polling.
  useEffect(() => {
    if (!finishedTransaction) return;

    const { id, consumption, startDate, endDate } = finishedTransaction;
    transactionIdRef.current = id;
    finalEnergyConsumedRef.current = consumption != null ? convertToKWh(consumption) : 0;
    setStartTime(startDate);
    setEndChargeTime(new Date(endDate));
    setFinalDuration(calculateDuration(startDate, endDate));
    setFinalTransactionData({ ongoingTransactionInfo: null });
    setChargingFinished(true);

    analyticsService.pushPageView('charging_completed', { station_id: evcpData?.reference });
  }, [finishedTransaction, evcpData]);

  return (
    <div className="flex justify-center items-center bg-white px-4">
      <div className="bg-white pt-32 flex flex-col items-center w-full px-4 pb-75">
        {!chargingFinished && (
          <div
            id="lottie-animation"
            style={{
              width: "300px",
              height: "100px",
              margin: "0 auto",
              marginTop: "10%",
            }}
          ></div>
        )}

        {chargingFinished ? (
          <p className="text-center text-xl font-medium mb-4 text-green-600">
            {t("startCharging.ended")}
          </p>
        ) : isCurrentlyCharging ? (
          <p className="text-center text-xl font-medium mb-4 text-blue-600">
            {t("startCharging.launched")}
          </p>
        ) : (
          <p className="text-center text-xl font-medium mb-4">
            {t("startCharging.launching")}
          </p>
        )}

        {/* Afficher les informations de la transaction si disponibles */}
        {(displayData && (displayData.ongoingTransactionInfo || chargingFinished)) && (
          <div className={`mb-4 p-4 rounded-lg w-full max-w-md ${chargingFinished ? 'bg-green-50' : 'bg-blue-50'}`}>
            <h3 className="text-lg font-medium mb-2">{t("startCharging.transactionInfo")}</h3>
            <div className="space-y-2 text-sm">
              <p><strong>{t("startCharging.status")} : </strong> 
                <span className={chargingFinished ? 'text-green-600' : 'text-blue-600'}>
                  {chargingFinished ? t("startCharging.chargeEnded") : t("startCharging.chargeInProgress")}
                </span>
              </p>
              <p><strong>{t("startCharging.energy")} : </strong> 
                {chargingFinished ? finalEnergyConsumedRef.current : (displayData.ongoingTransactionInfo ? convertToKWh(displayData.ongoingTransactionInfo.meterValue || 0) : 0)} kWh
              </p>
              <p><strong>{t("startCharging.duration")} : </strong> 
                {startTime ? 
                  (chargingFinished ? (finalDuration || calculateDuration(startTime, endChargeTime)) : currentDuration) : 
                  '0h 0min'
                }
              </p>
              {/* <p><strong>Puissance instantanée:</strong> {displayData.ongoingTransactionInfo?.instantaneousPower || 0} W</p>
              <p><strong>Puissance maximale:</strong> {displayData.ongoingTransactionInfo?.powerMax || 0} W</p> */}
            </div>
          </div>
        )}
        
        {/* remote-stop btn + unlock */}
        <div className="flex flex-col sm:flex-row gap-4 mb-4 w-full max-w-md">
          {isCurrentlyCharging && (
            <button
              onClick={handleStopCharge}
              disabled={isStoppingCharge}
              className="bg-red-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-red-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed relative flex-1"
            >
              {isStoppingCharge ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  <span>{t("startCharging.stoppingCharge")}</span>
                </div>
              ) : (
                t("startCharging.stopCharge")
              )}
            </button>
            
          )}
          
          {/* btn unlock visible que si la charge est terminee */}
          {chargingFinished && (
            <button
              onClick={handleUnlock}
              disabled={isUnlocking}
              className="bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed relative flex-1"
            >
              {isUnlocking ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  <span>{t("startCharging.unlocking")}</span>
                </div>
              ) : (
                t("startCharging.unlockConnector")
              )}
            </button>
          )}
        </div>

        {/* <div className="mb-4 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-gray-700">
            <strong>{t("startCharging.chargingStation")}:</strong> {evcpData?.name || evcpData?.reference}
          </p>
          <p className="text-sm text-gray-700">
            <strong>{t("startCharging.connector")}:</strong> {connector?.number} ({connector?.type})
          </p>
        </div> */}

        {!showFacture && (transactionData && transactionData.ongoingTransactionInfo !== null || chargingFinished) && (
          <div className="flex items-center mt-32 mb-4">
            <input
              id="invoiceCheckbox"
              type="checkbox"
              checked={showInvoiceFields}
              onChange={handleCheckboxChange}
              className="custom-checkbox"
            />
            <label htmlFor="invoiceCheckbox" className="label-invoice">
              {t("startCharging.wantInvoice")}
            </label>
          </div>
        )}
        {showSuccess && (
          <div>
            {!chargingFinished && (
              <>
                <h2 className="auth-title2 pb-4 pt-10">
                  <img
                    src="/Check_Circle.svg"
                    alt="Lock icon"
                    className="w-5 h-5"
                  />{" "}
                  {t("startCharging.detailsSaved")}
                </h2>
                <p className="text-center text-lg font-medium mb-4">
                  {t("startCharging.thankYou")}
                  <br /> {t("startCharging.invoiceMessage")}
                </p>
              </>
            )}

            <div className="w-full mt-32 max-w-md space-y-6">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">
                        {t("downloadApp.title")}
                    </h2>
                    <p className="text-gray-600 text-center">
                        {t("downloadApp.description")}
                    </p>
                </div>

                <div className="flex flex-col space-y-3">
                    <button
                        onClick={handleDownloadApp}
                        className="bg-indigo-600 text-white py-3 px-4 w-50 rounded-lg font-medium hover:bg-indigo-700 transition-colors"
                    >
                        {t("downloadApp.downloadButton")}
                    </button>
                </div>

                <div className="flex justify-center space-x-4">
                    <img
                        src="/google-play-badge.svg"
                        alt="Google Play"
                        className="h-10 cursor-pointer"
                        onClick={() => window.open('https://play.google.com/store/apps/details?id=com.qovoltis.client', '_blank')}
                    />
                    <img
                        src="/app-store-badge.svg"
                        alt="App Store"
                        className="h-10 cursor-pointer"
                        onClick={() => window.open('https://apps.apple.com/fr/app/qovoltis/1524983586', '_blank')}
                    />
                </div>

            </div>
          </div>
        )}

        {showInvoiceFields && (
          <div className="mb-4 w-full max-w-md mx-auto">
            {error && (
              <p className="text-red-600 text-sm text-center font-medium mb-4">{error}</p>
            )}
            <div className="input-container">
              <label
                className={`input-label ${fieldErrors.first_name ? "error" : ""}`}
              >
                {t("startCharging.firstName")}
              </label>
              <input
                name="first_name"
                className={`styled-input ${fieldErrors.first_name ? "input-error" : ""}`}
                value={formData.first_name}
                onChange={handleInputChange}
              />
              {fieldErrors.first_name && (
                <p className="error-message">{fieldErrors.first_name}</p>
              )}
            </div>

            <div className="input-container">
              <label
                className={`input-label ${fieldErrors.last_name ? "error" : ""}`}
              >
                {t("startCharging.lastName")}
              </label>
              <input
                name="last_name"
                className={`styled-input ${fieldErrors.last_name ? "input-error" : ""}`}
                value={formData.last_name}
                onChange={handleInputChange}
              />
              {fieldErrors.last_name && (
                <p className="error-message">{fieldErrors.last_name}</p>
              )}
            </div>

            <div className="input-container">
              <label
                className={`input-label ${fieldErrors.adresse ? "error" : ""}`}
              >
                {t("startCharging.address")}
              </label>
              <input
                name="adresse"
                className={`styled-input ${fieldErrors.adresse ? "input-error" : ""}`}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setTimeout(() => setIsFocused(false), 200)}
              />
              {fieldErrors.adresse && (
                <p className="error-message">{fieldErrors.adresse}</p>
              )}
              {suggestions.length > 0 && isFocused && (
                <ul className="border border-gray-300 rounded mt-2 bg-white z-50">
                  {suggestions.map((item) => (
                    <li
                      key={item.properties ? item.properties.id : item.id}
                      onClick={() => handleSelect(item)}
                      className="p-2 hover:bg-gray-100 cursor-pointer"
                    >
                      {item.properties ? item.properties.label : item.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="fixed bottom-0 left-0 right-0 text-center bg-white p-4 shadow-md footer-btn">
              <button onClick={handleValider} className={`custom-button w-full small-btn ${isDisabled ? "disabled-button" : ""}`} disabled={isDisabled} >
                <span className="custom-button-text">{t("startCharging.validate")}</span>
              </button>
            </div>

            <div className="text-center bg-white p-4 w-100">
              <img className="w-1/2 h-10 mx-auto" src="/logo_qovoltis.svg" alt="Qovoltis" />
            </div>

          </div>
        )}
        
      </div>
      
      {/* Modal de confirmation pour arrêter la charge */}
      <ConfirmationModal
        isOpen={showStopChargeModal}
        onClose={() => setShowStopChargeModal(false)}
        onConfirm={confirmStopCharge}
        title={t("startCharging.confirmStopCharge")}
        message={t("startCharging.confirmStopChargeMessage")}
      />
      
      {/* Modal de confirmation pour déverrouiller le connecteur */}
      <ConfirmationModal
        isOpen={showUnlockModal}
        onClose={() => setShowUnlockModal(false)}
        onConfirm={confirmUnlock}
        title={t("startCharging.confirmUnlock")}
        message={t("startCharging.confirmUnlockMessage")}
      />
    </div>
  );
}
